import { getEnumKeyByValue } from '@/shared/utils/enums';
import { createAddressSchema, createPhoneNumberSchema, ReportingType as ReportingTypeEnum, ROLE } from '@trace-8/trace8-schema';
import { z } from 'zod/v4';

export const basicInfoSchema = z.object({
  firmName: z.string({ error: 'This field is required' }).min(1, { error: 'This field is required' }),
  reportingType: z.enum([...Object.values(ReportingTypeEnum), '']).optional(),
  internalReviewFlag: z.boolean(),
  bulkAllocateFlag: z.boolean(),
  internalReviewFlagId: z.string().nullable(),
  bulkAllocateFlagId: z.string().nullable()
});

export const defaultUsersSchema = z.object({
  primaryAdmin: z.string({ error: 'This field is required' }),
  advisorTeamDefault: z.string({ error: 'This field is required' }),
  investmentTeamDefault: z.string({ error: 'This field is required' }),
  operationsTeamDefault: z.string({ error: 'This field is required' }),
  complianceTeamDefault: z.string({ error: 'This field is required' })
});

export const contactInfoSchema = z.object({
  email: z.email().or(z.literal('')).nullish(),
  website: z.string().or(z.literal('')).nullish(),
  phone: createPhoneNumberSchema({}, true).nullish(),
  address: createAddressSchema({}, true).nullish()
});

export enum FirmInfoSectionNameEnum {
  BasicInfo = 'basicInfo',
  DefaultUsers = 'defaultUsers',
  ContactInfo = 'contactInfo'
}

export interface IFirmInfoFormData {
  [FirmInfoSectionNameEnum.BasicInfo]: z.infer<typeof basicInfoSchema>;
  [FirmInfoSectionNameEnum.DefaultUsers]: z.infer<typeof defaultUsersSchema>;
  [FirmInfoSectionNameEnum.ContactInfo]: z.infer<typeof contactInfoSchema>;
}

export interface IFirmInfoSectionsData {
  title: string;
  name: FirmInfoSectionNameEnum;
  modalPageType: FirmInfoSectionNameEnum;
  formData: IFirmInfoFormData[keyof IFirmInfoFormData];
  displayData: { title: string; value: string | number | JSX.Element | null }[];
  isEmpty?: boolean;
}

export const userRoleMap = {
  primaryAdmin: [getEnumKeyByValue(ROLE, ROLE.ADMIN), getEnumKeyByValue(ROLE, ROLE.SUPER_ADMIN)],
  advisorTeamDefault: [
    getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN),
    getEnumKeyByValue(ROLE, ROLE.ADVISOR_LEAD),
    getEnumKeyByValue(ROLE, ROLE.ADVISOR_MEMBER),
    getEnumKeyByValue(ROLE, ROLE.ADVISOR_TEAM_DEFAULT),
    getEnumKeyByValue(ROLE, ROLE.ADVISOR_ASSOCIATE)
  ],
  investmentTeamDefault: [
    getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN),
    getEnumKeyByValue(ROLE, ROLE.INVESTMENT_LEAD),
    getEnumKeyByValue(ROLE, ROLE.INVESTMENT_MEMBER),
    getEnumKeyByValue(ROLE, ROLE.INVESTMENT_TEAM_DEFAULT)
  ],
  operationsTeamDefault: [
    getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN),
    getEnumKeyByValue(ROLE, ROLE.OPERATIONS_LEAD),
    getEnumKeyByValue(ROLE, ROLE.OPERATIONS_MEMBER),
    getEnumKeyByValue(ROLE, ROLE.OPERATIONS_TEAM_DEFAULT)
  ],
  complianceTeamDefault: [
    getEnumKeyByValue(ROLE, ROLE.PRIMARY_ADMIN),
    getEnumKeyByValue(ROLE, ROLE.COMPLIANCE_LEAD),
    getEnumKeyByValue(ROLE, ROLE.COMPLIANCE_MEMBER),
    getEnumKeyByValue(ROLE, ROLE.COMPLIANCE_TEAM_DEFAULT)
  ]
} as const;
